/**
 * Help & Support Page
 * 
 * Documentation and support resources for Tyaus.
 * Enterprise-grade professional design.
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Cpu,
  Zap,
  Copy,
  Check,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export default function HelpPage() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8 max-w-[1600px] mx-auto"
    >
      {/* Page Header */}
      <motion.header variants={itemVariants} className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight text-white mb-2">
              Help & Support
            </h1>
            <p className="text-sm sm:text-base text-surface-400 leading-relaxed">
              Documentation, guides, and resources to help you get the most out of Tyaus
            </p>
          </div>
        </div>
      </motion.header>

      {/* Documentation Content */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        {/* Introduction Card */}
        <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-8">
          <h2 className="text-2xl font-semibold text-white mb-4">
            Getting Started with Tyaus API
          </h2>
          <p className="text-surface-400 text-base leading-relaxed max-w-3xl">
            Use the Tyaus API to provision GPU compute resources programmatically for your AI/ML projects.
            First, create an API key from the{' '}
            <Link to="/api-keys" className="text-gpu-400 hover:text-gpu-300 font-medium transition-colors">
              API Keys page
            </Link>,{' '}
            then use one of the options below to integrate with your project.
          </p>
        </div>

        {/* SDK Options Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Option A: Python SDK */}
          <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-500/20 flex items-center justify-center">
                <span className="text-blue-400 font-bold text-lg">A</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Python SDK</h3>
                <p className="text-sm text-surface-500">Recommended</p>
              </div>
            </div>
            <p className="text-surface-400 text-sm mb-5 leading-relaxed">
              The easiest way to use Tyaus. Install the SDK and start provisioning GPUs with just a few lines of code.
            </p>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-surface-500 uppercase tracking-wide">Installation</span>
                  <button
                    onClick={() => copyToClipboard('cd adminDashboard/sdk-python\npip install -e .', 'install')}
                    className="p-1.5 rounded-md text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors"
                  >
                    {copiedCode === 'install' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <pre className="bg-slate-800/25 rounded-xl p-4 text-sm overflow-x-auto border border-surface-800/50">
                  <code className="text-surface-300 font-mono">{`cd adminDashboard/sdk-python
pip install -e .`}</code>
                </pre>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-surface-500 uppercase tracking-wide">Usage Example</span>
                  <button
                    onClick={() => copyToClipboard(`from gpu_platform import GPUPlatformClient

# Initialize with your API key
client = GPUPlatformClient(
    api_key="sk_live_your_key_here",
    base_url="http://localhost:3001"
)

# Create a GPU job for your AI training
response = client.create_job(
    instance_type="g4dn.xlarge",
    max_runtime_hours=4,
    use_spot=True,
    docker_image="pytorch/pytorch:2.0.0-cuda11.7-cudnn8-runtime",
    startup_command="python /workspace/train.py"
)

print(f"Job created: {response.job_id}")`, 'sdk')}
                    className="p-1.5 rounded-md text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors"
                  >
                    {copiedCode === 'sdk' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <pre className="bg-slate-800/25 rounded-xl p-4 text-sm overflow-x-auto border border-surface-800/50 max-h-72">
                  <code className="text-surface-300 font-mono">{`from gpu_platform import GPUPlatformClient

# Initialize with your API key
client = GPUPlatformClient(
    api_key="sk_live_your_key_here",
    base_url="http://localhost:3001"
)

# Create a GPU job
response = client.create_job(
    instance_type="g4dn.xlarge",
    max_runtime_hours=4,
    use_spot=True
)

print(f"Job created: {response.job_id}")`}</code>
                </pre>
              </div>
            </div>
          </div>

          {/* Option B: Direct API Calls */}
          <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center">
                <span className="text-emerald-400 font-bold text-lg">B</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">REST API</h3>
                <p className="text-sm text-surface-500">cURL / Fetch</p>
              </div>
            </div>
            <p className="text-surface-400 text-sm mb-5 leading-relaxed">
              Use the REST API directly from any language or tool that can make HTTP requests.
            </p>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-surface-500 uppercase tracking-wide">Create a GPU Job</span>
                  <button
                    onClick={() => copyToClipboard(`curl -X POST http://localhost:3001/api/v1/gpu-jobs \\
  -H "Authorization: Bearer sk_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"instanceType": "g4dn.xlarge", "maxRuntimeHours": 4}'`, 'curl-create')}
                    className="p-1.5 rounded-md text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors"
                  >
                    {copiedCode === 'curl-create' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <pre className="bg-slate-800/25 rounded-xl p-4 text-sm overflow-x-auto border border-surface-800/50">
                  <code className="text-surface-300 font-mono">{`curl -X POST localhost:3001/api/v1/gpu-jobs \\
  -H "Authorization: Bearer sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{"instanceType": "g4dn.xlarge"}'`}</code>
                </pre>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-surface-500 uppercase tracking-wide">List Jobs</span>
                  <button
                    onClick={() => copyToClipboard(`curl http://localhost:3001/api/v1/gpu-jobs \\
  -H "Authorization: Bearer sk_live_your_key_here"`, 'curl-list')}
                    className="p-1.5 rounded-md text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors"
                  >
                    {copiedCode === 'curl-list' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <pre className="bg-slate-800/25 rounded-xl p-4 text-sm overflow-x-auto border border-surface-800/50">
                  <code className="text-surface-300 font-mono">{`curl localhost:3001/api/v1/gpu-jobs \\
  -H "Authorization: Bearer sk_..."`}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* GPU Instance Types Table */}
        <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl overflow-hidden flex flex-col">
          <div className="px-6 py-5 border-b border-surface-800/60 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gpu-500/15 flex items-center justify-center flex-shrink-0">
                <Cpu className="w-5 h-5 text-gpu-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-white leading-tight">Available GPU Instance Types</h3>
                <p className="text-sm text-surface-500 leading-tight mt-0.5">Choose the right instance for your workload</p>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto flex-1">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-surface-800/40">
                  <th className="text-left py-4 px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider align-middle">Instance</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider align-middle">GPU</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider align-middle">Memory</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider align-middle">vCPUs</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider align-middle">Best For</th>
                  <th className="text-right py-4 px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider align-middle">Spot Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-800/30">
                {[
                  { type: 'g4dn.xlarge', gpu: '1x T4', mem: '16 GB', vcpu: '4', use: 'Inference, development', price: '~$0.16/hr' },
                  { type: 'g4dn.2xlarge', gpu: '1x T4', mem: '16 GB', vcpu: '8', use: 'Training, CPU-bound', price: '~$0.23/hr' },
                  { type: 'g5.xlarge', gpu: '1x A10G', mem: '24 GB', vcpu: '4', use: 'Training, fine-tuning', price: '~$0.40/hr' },
                  { type: 'g5.2xlarge', gpu: '1x A10G', mem: '24 GB', vcpu: '8', use: 'Larger models', price: '~$0.58/hr' },
                  { type: 'p3.2xlarge', gpu: '1x V100', mem: '16 GB', vcpu: '8', use: 'ML training, research', price: '~$0.92/hr' },
                  { type: 'p4d.24xlarge', gpu: '8x A100', mem: '320 GB', vcpu: '96', use: 'Foundation models', price: '~$10.00/hr' },
                ].map((row) => (
                  <tr key={row.type} className="hover:bg-surface-800/30 transition-colors">
                    <td className="py-4 px-6 font-mono text-sm text-gpu-400 font-medium align-middle">{row.type}</td>
                    <td className="py-4 px-6 text-sm text-surface-300 align-middle">{row.gpu}</td>
                    <td className="py-4 px-6 text-sm text-surface-300 align-middle">{row.mem}</td>
                    <td className="py-4 px-6 text-sm text-surface-300 align-middle">{row.vcpu}</td>
                    <td className="py-4 px-6 text-sm text-surface-500 align-middle">{row.use}</td>
                    <td className="py-4 px-6 text-sm text-emerald-400 text-right font-medium align-middle">{row.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t border-surface-800/40 bg-surface-900/50 flex-shrink-0">
            <p className="text-xs text-surface-500 leading-tight">
              * Spot prices are approximate and vary by region and availability
            </p>
          </div>
        </div>

        {/* Quick Tips */}
        <div className="bg-gradient-to-br from-gpu-600/10 via-surface-900 to-teal-600/10 border border-gpu-500/20 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
            <Zap className="w-5 h-5 text-gpu-400" />
            Quick Tips
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Use Spot Instances', desc: 'Save up to 70% by using spot instances for non-critical workloads.' },
              { title: 'Set Max Runtime', desc: 'Always set a max runtime to avoid unexpected charges.' },
              { title: 'Cancel When Done', desc: 'Remember to cancel jobs when training completes to stop billing.' },
              { title: 'Use Docker Images', desc: 'Specify a Docker image with dependencies pre-installed for faster startup.' },
            ].map((tip) => (
              <div key={tip.title} className="flex items-start gap-3 p-4 rounded-xl bg-slate-800/25 border border-surface-800/40">
                <div className="w-1.5 h-1.5 rounded-full bg-gpu-400 mt-2 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-surface-200 mb-1">{tip.title}</h4>
                  <p className="text-sm text-surface-500 leading-relaxed">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
